/**
 * Better Auth API Service
 * Feature: 008-better-auth-integration
 *
 * Client-side authentication service for Better Auth API.
 * Provides sign-in, sign-out, session management, and OAuth flows.
 *
 * @see Backend Feature 005 for API implementation
 */

import * as Sentry from '@sentry/react';
import {
  type BetterAuthSession,
  type BetterAuthUser,
  type BetterAuthSessionResponse,
  type SignInEmailRequest,
  type SignInEmailResponse,
  type SignOutResponse,
  isSessionExpired,
  sessionNeedsRefresh,
} from '../../types/betterAuth';
import {
  AuthError,
  AuthErrorType,
  mapApiErrorToType,
} from '../../types/authErrors';

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  throw new Error('VITE_API_BASE_URL environment variable is required');
}

/**
 * Auth API base URL - uses versioned /v1/auth paths (Feature 006 migration).
 * Falls back to deriving from API_BASE_URL if not explicitly set.
 */
const AUTH_BASE_URL = import.meta.env.VITE_AUTH_API_URL || API_BASE_URL.replace(/\/v1\/?$/, '');

/** LocalStorage key for session token */
const SESSION_TOKEN_KEY = 'better_auth_session_token';

/** LocalStorage key for cached session data */
const SESSION_DATA_KEY = 'better_auth_session_data';

/** Default request timeout */
const REQUEST_TIMEOUT = 30000;

// ============================================================================
// Token Storage Utilities
// ============================================================================

/**
 * Get stored session token from localStorage.
 */
export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'auth', action: 'getStoredToken' },
    });
    return null;
  }
}

/**
 * Store session token in localStorage.
 */
export function setStoredToken(token: string): void {
  try {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'auth', action: 'setStoredToken' },
    });
  }
}

/**
 * Clear stored session token from localStorage.
 */
export function clearStoredToken(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(SESSION_DATA_KEY);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'auth', action: 'clearStoredToken' },
    });
  }
}

/**
 * Get cached session data from localStorage.
 * Used for quick app initialization before API validation.
 */
export function getCachedSessionData(): BetterAuthSessionResponse | null {
  try {
    const data = localStorage.getItem(SESSION_DATA_KEY);
    if (!data) return null;
    
    const parsed = JSON.parse(data) as BetterAuthSessionResponse;
    
    // Check if session is expired
    if (parsed.session && isSessionExpired(parsed.session)) {
      clearStoredToken();
      return null;
    }
    
    return parsed;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'auth', action: 'getCachedSessionData' },
    });
    return null;
  }
}

/**
 * Cache session data in localStorage.
 */
export function setCachedSessionData(data: BetterAuthSessionResponse): void {
  try {
    localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(data));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'auth', action: 'setCachedSessionData' },
    });
  }
}

// ============================================================================
// Request Helpers
// ============================================================================

/**
 * Build full API URL for auth endpoints.
 * Uses AUTH_BASE_URL (without /v1) for auth endpoints.
 */
function buildAuthUrl(endpoint: string): string {
  // Ensure base URL ends with / and endpoint doesn't start with /
  const baseUrl = AUTH_BASE_URL.endsWith('/') ? AUTH_BASE_URL : `${AUTH_BASE_URL}/`;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${baseUrl}${cleanEndpoint}`;
}

/**
 * Parse error response from API.
 */
async function parseErrorResponse(response: Response): Promise<AuthError> {
  try {
    const errorBody = await response.json();
    const type = mapApiErrorToType(errorBody.code);
    return new AuthError(
      type,
      errorBody.message || 'An unexpected error occurred',
      errorBody.code || 'UNKNOWN',
      response.status,
      errorBody.details
    );
  } catch {
    // Response body may not be JSON
    return new AuthError(
      mapApiErrorToType(undefined),
      response.statusText || 'An unexpected error occurred',
      'UNKNOWN',
      response.status
    );
  }
}

/**
 * Make an auth API request with proper error handling.
 */
async function authRequest<T>(
  endpoint: string,
  options: RequestInit & { noAuth?: boolean } = {}
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Add authorization header if we have a token and noAuth is not set
  if (!options.noAuth) {
    const token = getStoredToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  try {
    const response = await fetch(buildAuthUrl(endpoint), {
      ...options,
      headers,
      credentials: 'include', // Include cookies for session
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error = await parseErrorResponse(response);
      
      Sentry.logger.error('Auth API error', {
        endpoint,
        status: response.status,
        errorType: error.type,
        errorCode: error.code,
      });
      
      throw error;
    }

    // Handle empty responses
    if (response.status === 204) {
      return undefined as T;
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);

    // Re-throw AuthError as-is
    if (error instanceof AuthError) {
      throw error;
    }

    // Handle network/fetch errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      const authError = AuthError.networkError(new Error('Request timeout'));
      Sentry.captureException(authError, {
        tags: { component: 'auth', action: 'request_timeout', endpoint },
      });
      throw authError;
    }

    const authError = AuthError.networkError(error as Error);
    Sentry.captureException(authError, {
      tags: { component: 'auth', action: 'network_error', endpoint },
    });
    throw authError;
  }
}

// ============================================================================
// Authentication API Functions
// ============================================================================

/**
 * Sign in with email and password.
 * POST /v1/auth/sign-in/email
 *
 * @param credentials - Email and password
 * @returns Session and user data
 * @throws AuthError on failure
 */
export async function signInWithEmail(
  credentials: SignInEmailRequest
): Promise<SignInEmailResponse> {
  Sentry.logger.info('Attempting email sign-in', { email: credentials.email });

  const response = await authRequest<SignInEmailResponse>(
    'v1/auth/sign-in/email',
    {
      method: 'POST',
      body: JSON.stringify(credentials),
      noAuth: true, // No auth needed for sign-in
    }
  );

  // Store session token (Better Auth returns token at root level)
  setStoredToken(response.token);
  
  // Cache session data for quick access
  // Note: Better Auth sign-in response has token at root, not nested in session
  setCachedSessionData({
    session: {
      token: response.token,
      id: '', // Will be populated on next getSession call
      userId: response.user.id,
      expiresAt: '', // Will be populated on next getSession call
      createdAt: new Date().toISOString(),
      userAgent: null,
      ipAddress: null,
    },
    user: response.user,
  });

  Sentry.logger.info('Email sign-in successful', {
    userId: response.user.id,
    isAdmin: response.user.role?.includes('admin'),
  });

  return response;
}

/**
 * Sign out and invalidate session.
 * POST /v1/auth/sign-out
 *
 * @throws AuthError on failure (but always clears local session)
 */
export async function signOut(): Promise<void> {
  Sentry.logger.info('Signing out');

  try {
    await authRequest<SignOutResponse>('v1/auth/sign-out', {
      method: 'POST',
    });
  } catch (error) {
    // Log the error but still clear local session
    Sentry.logger.warn('Sign-out API call failed, clearing local session', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  } finally {
    // Always clear local session
    clearStoredToken();
  }

  Sentry.logger.info('Sign-out complete');
}

/**
 * Get current session from API.
 * GET /v1/auth/get-session
 *
 * @returns Session and user data, or null if not authenticated
 */
export async function getSession(): Promise<BetterAuthSessionResponse | null> {
  const token = getStoredToken();
  
  if (!token) {
    return null;
  }

  try {
    const response = await authRequest<BetterAuthSessionResponse>(
      'v1/auth/get-session',
      { method: 'GET' }
    );

    // Update cached data
    if (response.session && response.user) {
      setCachedSessionData(response);
    }

    return response;
  } catch (error) {
    if (error instanceof AuthError) {
      // Clear local session on auth errors
      if (
        error.type === AuthErrorType.SESSION_EXPIRED ||
        error.type === AuthErrorType.INVALID_SESSION ||
        error.type === AuthErrorType.UNAUTHORIZED
      ) {
        clearStoredToken();
        return null;
      }
    }
    throw error;
  }
}

/**
 * Validate current session.
 * Returns true if session is valid, false otherwise.
 */
export async function validateSession(): Promise<boolean> {
  try {
    const session = await getSession();
    return session !== null && session.session !== null;
  } catch {
    return false;
  }
}

/**
 * Check if session needs refresh (less than 1 day remaining).
 * Used for lazy refresh before API requests.
 */
export function checkSessionNeedsRefresh(): boolean {
  const cached = getCachedSessionData();
  if (!cached?.session) return false;
  return sessionNeedsRefresh(cached.session);
}

// ============================================================================
// OAuth Functions
// ============================================================================

/**
 * Initiate Google OAuth sign-in.
 * Redirects to OAuth provider.
 */
export function signInWithGoogle(callbackUrl?: string): void {
  Sentry.logger.info('Initiating Google OAuth');

  const url = new URL(buildAuthUrl('v1/auth/sign-in/social'));
  url.searchParams.set('provider', 'google');
  
  if (callbackUrl) {
    url.searchParams.set('callbackURL', callbackUrl);
  } else {
    // Default callback URL for admin portal
    url.searchParams.set(
      'callbackURL',
      `${window.location.origin}/admin/auth/callback`
    );
  }

  window.location.href = url.toString();
}

/**
 * Handle OAuth callback.
 * Called when returning from OAuth provider.
 *
 * @returns Session response if successful, null if cancelled
 * @throws AuthError on failure
 */
export async function handleOAuthCallback(): Promise<BetterAuthSessionResponse | null> {
  Sentry.logger.info('Handling OAuth callback');

  // Check for errors in URL
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  if (error) {
    if (error === 'access_denied') {
      Sentry.logger.info('OAuth cancelled by user');
      return null;
    }

    const authError = new AuthError(
      AuthErrorType.OAUTH_ERROR,
      errorDescription || 'OAuth authentication failed',
      error,
      400
    );
    
    Sentry.captureException(authError, {
      tags: { component: 'auth', action: 'oauth_callback' },
      extra: { error, errorDescription },
    });
    
    throw authError;
  }

  // Get session after OAuth redirect
  const session = await getSession();
  
  if (!session?.session || !session?.user) {
    throw new AuthError(
      AuthErrorType.OAUTH_ERROR,
      'Failed to get session after OAuth',
      'OAUTH_SESSION_ERROR',
      401
    );
  }

  return session;
}

// ============================================================================
// Session Token Access (for API Client)
// ============================================================================

/**
 * Get access token for API requests.
 * Used by the API client for Authorization header.
 * 
 * This replaces the Supabase getAccessToken function.
 */
export async function getAccessToken(): Promise<string | null> {
  const token = getStoredToken();
  
  if (!token) {
    return null;
  }

  // Check if session needs refresh (lazy refresh)
  if (checkSessionNeedsRefresh()) {
    try {
      // Validate session (this will refresh if needed)
      const session = await getSession();
      return session?.session?.token ?? null;
    } catch {
      // On error, return existing token (let the request fail if invalid)
      return token;
    }
  }

  return token;
}

/**
 * Check if user has a valid session (without API call).
 * Uses cached data for quick checks.
 */
export function hasValidSession(): boolean {
  const cached = getCachedSessionData();
  if (!cached?.session) return false;
  return !isSessionExpired(cached.session);
}

/**
 * Get cached user data (without API call).
 * Returns null if no valid session.
 */
export function getCachedUser(): BetterAuthUser | null {
  const cached = getCachedSessionData();
  if (!cached?.session || isSessionExpired(cached.session)) return null;
  return cached.user;
}

/**
 * Get cached session data (without API call).
 * Returns null if no valid session.
 */
export function getCachedSession(): BetterAuthSession | null {
  const cached = getCachedSessionData();
  if (!cached?.session || isSessionExpired(cached.session)) return null;
  return cached.session;
}
